"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dialog = Dialog;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const motion_1 = require("../lib/motion");
const overlay_1 = require("../lib/overlay");
const button_1 = require("./button");
const icon_1 = require("./icon");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Centred modal. Short, decisive flows only.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * New task, log time, confirm delete. Anything with a property rail or more than one section is
 * a `Sheet`.
 *
 * ⚠️ **`useId()` for the title id, and this is a fix rather than a transcription.** The design
 * system's implementation hardcodes `id="dlg-title"` and points `aria-labelledby` at it. Two
 * dialogs in the tree — and a confirm opened from inside another dialog is a designed flow here
 * (`decisions.md` T-1) — produce duplicate ids, at which point `getElementById` semantics pick
 * the first and the inner dialog is announced with the outer one's title. Worse than unlabelled,
 * because it is confidently wrong.
 *
 * ⚠️ **`aria-labelledby` is omitted entirely when there is no title**, rather than pointing at
 * an element that does not exist. A dangling `aria-labelledby` is not "no name" — it is a
 * broken reference, and assistive tech behaviour for one is unspecified. `bare` dialogs are the
 * case that hits this: they render no header at all.
 *
 * ⚠️ **The scrim is `--scrim`, and it used to be `bg-black/50`.** A
 * scrim darkens what is behind it in *both* themes; `bg-foreground/50` would be a WHITE veil in
 * dark mode, which reads as a rendering fault. This is the one place raw black is right.
 *
 * ⚠️ **`shadow-modal`, not `shadow-lg`.** The design system's readme assigns `--shadow-modal` to
 * dialogs and sheets; its implementation reaches for Tailwind's default because the token was
 * never bridged. It is bridged now — see `base.css`.
 */
function Dialog({ open, onOpenChange, title, description, footer, width, maxHeight, bare = false, closeLabel, children, }) {
    const scope = (0, react_1.useId)();
    const titleId = `${scope}-title`;
    const descriptionId = `${scope}-description`;
    const close = () => onOpenChange?.(false);
    const { ref, nested } = (0, overlay_1.useModalSurface)(open, close);
    /* ⚠️ Held for its exit, `inert` while it plays — the trap, Escape and focus restore have already
       let go, because `useModalSurface` keys off `open` and not off this. See `motion.ts`. */
    const { mounted, closing } = (0, motion_1.usePresence)(open, motion_1.EXIT_MS.dialog);
    if (!mounted)
        return null;
    const labelled = !bare && title !== undefined && title !== null;
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { 
        /**
         * ⚠️⚠️ **CENTRED, AND THE TOP OFFSET IT REPLACED WAS THE HALF THAT BROKE.** This was
         * `items-start pt-[12vh]`, which pins a dialog twelve percent down the window and then gives
         * it the remaining eighty-eight to grow into — so a tall one runs off the bottom while there
         * is empty space above it. Centring costs that dialog nothing and gives a tall one the whole
         * window, which is what makes `max-h-full` on the panel enough on its own.
         *
         * ⚠️ **Both branches now agree**, so every dialog in the product sits in the same place — the
         * product owner's *"like other modals"*, 4 Sep 2026.
         */
        className: (0, cn_1.cn)('fixed inset-0 z-50 flex items-center justify-center', bare ? 'p-6' : 'p-4'), inert: closing, children: [(0, jsx_runtime_1.jsx)("div", { className: (0, cn_1.cn)('fixed inset-0', nested ? 'bg-[color:var(--scrim-nested)]' : 'bg-[color:var(--scrim)]', closing ? 'scrim-out' : 'scrim-in'), "aria-hidden": true, onClick: close }), (0, jsx_runtime_1.jsxs)("div", { ref: ref, role: "dialog", "aria-modal": "true", "aria-labelledby": labelled ? titleId : undefined, "aria-describedby": description === undefined ? undefined : descriptionId, tabIndex: -1, 
                /**
                 * ⚠️⚠️ **`max-h-full`, AND ITS ABSENCE MADE A DIALOG UNUSABLE RATHER THAN UGLY.** The
                 * height cap lived in the `style` below, which was only built when a `width` was passed — so
                 * every dialog that took the default width had **no cap at all** and grew past the bottom of
                 * the window. The project dialog crossed that line the day it gained a second tracker box:
                 * the Save button rendered below the viewport, with nothing to scroll, and the form could be
                 * filled in but not submitted.
                 *
                 * ⚠️ **`full` resolves against the container**, which is `inset-0` minus its own padding — so
                 * the cap follows the `pt-[12vh]` offset instead of restating it as a magic number that goes
                 * stale the moment that padding changes.
                 */
                className: (0, cn_1.cn)('bg-background shadow-modal relative z-50 flex max-h-full w-full flex-col rounded-lg border outline-none', closing ? 'dialog-out' : 'dialog-in', bare ? 'overflow-hidden' : 'gap-4 p-5', width === undefined && 'max-w-lg'), 
                /* ⚠️ Both are overrides now, and either may be given without the other. */
                style: {
                    /* ⚠️ **`maxWidth` only for a NUMBER.** A CSS length is expected to carry its own bound —
                       `min(1320px, 94vw)` already does — and forcing `96vw` over it would silently override a
                       caller who had been more careful than that. */
                    ...(width === undefined
                        ? {}
                        : typeof width === 'number'
                            ? { width, maxWidth: '96vw' }
                            : { width }),
                    ...(maxHeight === undefined ? {} : { maxHeight }),
                }, children: [bare ? null : ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-1.5", children: [(0, jsx_runtime_1.jsx)("div", { id: titleId, className: "text-base font-semibold", children: title }), description === undefined ? null : ((0, jsx_runtime_1.jsx)("div", { id: descriptionId, className: "text-muted-foreground text-[13px]", children: description }))] })), bare ? ((0, jsx_runtime_1.jsx)("div", { className: "flex min-h-0 flex-1 flex-col overflow-hidden", children: children })) : (
                    /**
                     * ⚠️ **The body scrolls; the title and the footer do not.** A capped dialog whose body
                     * could not scroll would simply clip its own form, which is the same bug wearing a
                     * scrollbar's clothes. `min-h-0` is what lets a flex child shrink below its content —
                     * without it `overflow-y-auto` never engages and the cap is ignored.
                     */
                    /**
                     * ⚠️⚠️ **`px-1 -mx-1` IS THE FOCUS RING'S ROOM, AND WITHOUT IT EVERY FULL-WIDTH FIELD IN
                     * A DIALOG IS CLIPPED DOWN BOTH SIDES.** `field.tsx` draws focus as
                     * `focus-visible:ring-[3px]` OUTSIDE the input's border box, and CSS will not let one
                     * axis overflow visibly while the other scrolls — `overflow-y: auto` computes
                     * `overflow-x` to `auto` too, so the ring is cut exactly where the body ends.
                     *
                     * ⚠️ The padding and the negative margin cancel, so nothing moves: the box grows 4px into
                     * the dialog's own `p-5` and the content stays where it was. Reported as *"a weird
                     * clipping on the input fields in some modals, where the highlight border's left and
                     * right edge gets cut off"* — it was every modal with a scrolling body.
                     */
                    (0, jsx_runtime_1.jsx)("div", { className: "min-h-0 flex-1 overflow-y-auto px-1 -mx-1", children: children })), footer === undefined ? null : ((0, jsx_runtime_1.jsx)("div", { className: "flex items-center justify-end gap-2", children: footer })), bare ? null : ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", onClick: close, "aria-label": closeLabel, className: "absolute top-3 right-3", children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "x", size: 15 }) }))] })] }), document.body);
}
//# sourceMappingURL=dialog.js.map